import { Typography } from "@mui/material";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";

interface GenerationProgressProps {
  title: string;
  description: string;
  progress: number;
  stage: string;
  timestamp?: string;
  jobType: "icpResearch" | "adStrategy" | "adInsights" | "competitiveAnalysis";
  jobState: "initializing" | "in-progress" | "completed" | "error";
  error?: string;
}

export const GenerationProgress = ({
  title = "Generating...",
  description = "Please wait while we analyze your data...",
  progress = 0,
  stage = "",
  timestamp,
  jobType,
  jobState,
  error,
}: GenerationProgressProps) => {
  const formattedTimestamp = timestamp
    ? new Date(timestamp).toLocaleString()
    : null;

  const getIcon = () => {
    switch (jobState) {
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      default:
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
    }
  };

  const getBackgroundColor = () => {
    switch (jobState) {
      case "error":
        return "bg-red-100";
      case "completed":
        return "bg-green-100";
      default:
        return "bg-blue-100";
    }
  };

  const getProgressColor = () => {
    switch (jobState) {
      case "error":
        return "bg-red-600";
      case "completed":
        return "bg-green-600";
      default:
        return "bg-blue-600";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white rounded-lg border border-gray-200 p-6 mb-6"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`h-8 w-8 rounded-full ${getBackgroundColor()} flex items-center justify-center`}
        >
          {getIcon()}
        </div>
        <Typography variant="h6">{title}</Typography>
      </div>

      <div className="space-y-4">
        <div className="progress-item">
          <Typography variant="body1" color="text.secondary">
            {jobState === "error" ? error : description}
          </Typography>
        </div>

        <div className="progress-item">
          <Typography variant="subtitle2" className="text-blue-600">
            Job Type:
          </Typography>
          <Typography variant="body2" className="capitalize">
            {jobType.replace("-", " ")}
          </Typography>
        </div>

        <div className="progress-item">
          <Typography variant="subtitle2" className="text-blue-600">
            Current Stage:
          </Typography>
          <Typography variant="body2">{stage || jobState}</Typography>
        </div>

        {formattedTimestamp && (
          <div className="progress-item">
            <Typography variant="subtitle2" className="text-blue-600">
              Last Update:
            </Typography>
            <Typography variant="body2">{formattedTimestamp}</Typography>
          </div>
        )}

        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-300 ${getProgressColor()}`}
            style={{
              width: `${
                jobState === "completed"
                  ? 100
                  : Math.max(0, Math.min(100, progress))
              }%`,
            }}
          />
        </div>

        <Typography variant="body2" className="text-right text-gray-600">
          {jobState === "completed"
            ? "100"
            : Math.max(0, Math.min(100, progress))}
          % Complete
        </Typography>
      </div>
    </motion.div>
  );
};

export default GenerationProgress;
